import { useEffect } from 'react';
import type { Key } from 'react';

import { findNodeByValueField } from '@src/utils/tree';
import { constantStore } from '@stores/constantStore';
import { TABLE_CONFIG } from '@stores/ksnmTableStore/ksnmTableStore';
import { passingControlStore } from '@stores/passingControlStore';
import type { CompanyReportDto } from '@src/api';

interface IOpenGroup {
  value: string | undefined;
  children: IOpenGroup[];
}

const { TABLE_NAMES } = constantStore;

const recursiveFind = (id: Key, data: CompanyReportDto[]): CompanyReportDto[] | null => {
  for (const obj of data) {
    if (obj.id === id) {
      return [obj];
    }
    if (obj.children && obj.children.length > 0) {
      const result = recursiveFind(id, obj.children);
      if (result) {
        return [{ ...obj }, ...result];
      }
    }
  }
  return null;
};

const buildTree = (arr: CompanyReportDto[]) => {
  if (arr.length === 0) return [];

  // создаем первый корень дерева
  const root: IOpenGroup = { value: arr[0].name, children: [] };
  let currentNode = root;

  // с 1 элемента идем по массиву и вкладываем в children
  for (let i = 1; i < arr.length; i++) {
    const newNode = { value: arr[i].name, children: [] };
    currentNode.children.push(newNode);
    currentNode = newNode; // переходим глубже к новому узлу
  }

  // возвращаем массив с одним корнем
  return [root];
};

// если переход произошел из контроля данных, то должны раскрыть дерево до пакета из которого был переход
export const usePushFromDataControl = (update: () => void): void => {
  const { controlReports } = passingControlStore;
  const storage = localStorage.getItem('pushFromDataControl');
  const parseStorage = storage ? JSON.parse(storage) : null;

  useEffect(() => {
    if (parseStorage) {
      const id = findNodeByValueField(parseStorage.packageCode, controlReports as never, 'name')?.id;

      if (id) {
        const nodes = recursiveFind(id, controlReports);

        const lsData = localStorage.getItem(TABLE_CONFIG);
        const config = lsData ? JSON.parse(lsData) : {};

        if (nodes && config?.[TABLE_NAMES.PASSING_CONTROL]) {
          const openGroup: IOpenGroup[] = buildTree(nodes);

          localStorage.setItem(
            TABLE_CONFIG,
            JSON.stringify({
              ...config,
              [TABLE_NAMES.PASSING_CONTROL]: {
                ...config[TABLE_NAMES.PASSING_CONTROL],
                tableRequest: {
                  ...config[TABLE_NAMES.PASSING_CONTROL].tableRequest,
                  openGroup,
                },
              },
            }),
          );

          update();
          localStorage.removeItem('pushFromDataControl');
        }
      }
    }
  }, [controlReports, parseStorage, update]);
};
